import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import type { CostEstimate, Deal, Quote } from '@elka/types';
import { useDeals } from '../../quotes/hooks/useDeals';
import { useQuotes } from '../../quotes/hooks/useQuotes';
import { useCostEstimates } from '../../cost-estimates/hooks/useCostEstimates';
import { buildDuplicateDealInput, buildDuplicateEstimateInput } from '../lib/duplicateDeal';

async function getLatest<T>(
  collectionPath: string,
  dealId: string
): Promise<(T & { id: string }) | null> {
  const q = query(
    collection(db, ...splitPath(collectionPath)),
    where('dealId', '==', dealId),
    orderBy('createdAt', 'desc'),
    limit(1)
  );
  const snap = await getDocs(q);
  const first = snap.docs[0];
  return first ? ({ id: first.id, ...first.data() } as T & { id: string }) : null;
}

export function useDuplicateDeal() {
  // Modal-only usage — mounts (and these subscribe) only while the modal is open.
  const { addDeal } = useDeals();
  const { addCostEstimate } = useCostEstimates({ subscribe: false });
  const { cloneQuoteAsDraft } = useQuotes();

  const duplicateDeal = async (
    sourceDealId: string,
    target: { name: string; companyId: string; companyName: string }
  ): Promise<string> => {
    const dealSnap = await getDoc(doc(db, ...splitPath(COLLECTIONS.crm.deals), sourceDealId));
    if (!dealSnap.exists()) throw new Error('Source deal not found');
    const sourceDeal = { id: dealSnap.id, ...dealSnap.data() } as Deal;

    const [sourceEstimate, sourceQuote] = await Promise.all([
      getLatest<CostEstimate>(COLLECTIONS.quotes.costEstimates, sourceDealId),
      getLatest<Quote>(COLLECTIONS.quotes.quotes, sourceDealId),
    ]);

    // ponytail: sequential writes, a mid-way failure leaves a partial deal the user can delete
    const newDealId = await addDeal(buildDuplicateDealInput(sourceDeal, target));

    let newEstimateId: string | undefined;
    if (sourceEstimate) {
      newEstimateId = await addCostEstimate(
        buildDuplicateEstimateInput(sourceEstimate, {
          dealId: newDealId,
          dealName: target.name,
          companyId: target.companyId,
          companyName: target.companyName,
        })
      );
    }

    if (sourceQuote && newEstimateId) {
      const sameCompany = target.companyId === sourceDeal.companyId;
      await cloneQuoteAsDraft(sourceQuote, {
        dealId: newDealId,
        costEstimateId: newEstimateId,
        companyId: target.companyId,
        companyName: target.companyName,
        projectName: target.name,
        ...(sameCompany && sourceDeal.contactId !== undefined
          ? { contactId: sourceDeal.contactId }
          : {}),
        ...(sameCompany && sourceDeal.contactName !== undefined
          ? { contactName: sourceDeal.contactName }
          : {}),
      });
    }

    return newDealId;
  };

  return { duplicateDeal };
}
